import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import { Spinner } from 'flowbite-react'
import FamilyTreeEngine from '../../utils/familyTreeEngine'

const TreeCanvas = forwardRef(function TreeCanvas({ text, manualRoot, onRootChange, onTreeStats, onInfoHover }, ref) {
  const containerRef = useRef(null)
  const engineRef = useRef(null)
  const callbacksRef = useRef({ onRootChange, onTreeStats, onInfoHover })
  const [error, setError] = useState(null)
  const [rendering, setRendering] = useState(true)

  // Keep callbacks ref up to date without triggering re-render
  useEffect(() => {
    callbacksRef.current = { onRootChange, onTreeStats, onInfoHover }
  }, [onRootChange, onTreeStats, onInfoHover])

  // Expose setDetailLevel to parent via ref
  useImperativeHandle(ref, () => ({
    setDetailLevel(value) {
      if (engineRef.current) {
        engineRef.current.setDetailLevel(value)
      }
    }
  }), [])

  // Create/destroy engine when text changes
  useEffect(() => {
    if (!containerRef.current || !text) return

    setError(null)
    setRendering(true)

    const engine = new FamilyTreeEngine(containerRef.current, {
      lineHeight: 170,
      paddingAmount: 8,
      onRootChange: (...args) => callbacksRef.current.onRootChange?.(...args),
      onTreeStats: (...args) => callbacksRef.current.onTreeStats?.(...args),
      onInfoHover: (...args) => callbacksRef.current.onInfoHover?.(...args),
    })

    try {
      engine.load(text)
      engineRef.current = engine
    } catch (err) {
      console.error('Tree error:', err)
      setError(err.message)
    } finally {
      setRendering(false)
    }

    return () => {
      engine.destroy()
      if (engineRef.current === engine) {
        engineRef.current = null
      }
    }
  }, [text])

  // Update root without recreating engine
  useEffect(() => {
    if (manualRoot && engineRef.current) {
      engineRef.current.setRoot(manualRoot)
    }
  }, [manualRoot])

  if (error) {
    return (
      <div className="p-4 m-4 text-red-600 bg-red-50 rounded-lg dark:bg-red-900/30 dark:text-red-400">
        <p className="font-semibold">Error rendering tree:</p>
        <p className="text-sm mt-1 whitespace-pre-wrap">{error}</p>
        <p className="text-xs mt-2 text-gray-600 dark:text-gray-400">
          Try selecting a different person from the "Set tree root" dropdown above.
        </p>
      </div>
    )
  }

  return (
    <div style={{ position: 'relative', minHeight: '70vh' }}>
      {rendering && (
        <div className="absolute inset-0 flex flex-col justify-center items-center gap-3 z-10">
          <Spinner size="xl" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Rendering tree...</p>
        </div>
      )}
      <div
        ref={containerRef}
        className="tree-container"
        style={{ position: 'relative', minHeight: '70vh', overflow: 'auto' }}
      />
    </div>
  )
})

export default TreeCanvas
